
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "@/lib/types";

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  isRead: boolean;
  contractId?: string;
  createdAt: Date;
  type: "reminder" | "system" | "info";
}

export function useNotifications() {
  const queryClient = useQueryClient();

  const fetchNotifications = async (): Promise<Notification[]> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error(`Error fetching notifications: ${error.message}`);
      throw error;
    }

    return (data || []).map(item => ({
      id: item.id,
      userId: item.user_id,
      title: item.title,
      message: item.message,
      isRead: item.is_read,
      contractId: item.contract_id || undefined,
      createdAt: new Date(item.created_at),
      type: item.type as "reminder" | "system" | "info"
    }));
  };

  const markAsRead = async (notificationId: string): Promise<void> => {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);

    if (error) {
      toast.error(`Error marking notification as read: ${error.message}`);
      throw error;
    }

    toast.success("Notification marked as read");
  };

  const markAllAsRead = async (): Promise<void> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", userId)
      .eq("is_read", false);

    if (error) {
      toast.error(`Error marking all notifications as read: ${error.message}`);
      throw error;
    }

    toast.success("All notifications marked as read");
  };

  const deleteNotification = async (notificationId: string): Promise<void> => {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);

    if (error) {
      toast.error(`Error deleting notification: ${error.message}`);
      throw error;
    }

    toast.success("Notification deleted");
  };

  const deleteReadNotifications = async (): Promise<void> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("user_id", userId)
      .eq("is_read", true);

    if (error) {
      toast.error(`Error deleting read notifications: ${error.message}`);
      throw error;
    }

    toast.success("Read notifications cleared");
  };

  // Add the missing getUnreadCount function
  const getUnreadCount = async (): Promise<number> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      return 0;
    }

    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("is_read", false);

    if (error) {
      console.error(`Error counting unread notifications: ${error.message}`);
      return 0;
    }

    return count || 0;
  };

  // Queries
  const useNotificationsQuery = () => useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
  });

  const useUnreadCountQuery = () => useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: getUnreadCount,
    refetchInterval: 60000, // Refetch every minute
  });

  // Mutations
  const useMarkAsReadMutation = () => useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
    },
  });

  const useMarkAllAsReadMutation = () => useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
    },
  });

  const useDeleteNotificationMutation = () => useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
    },
  });

  const useDeleteReadNotificationsMutation = () => useMutation({
    mutationFn: deleteReadNotifications,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
    },
  });

  return {
    useNotificationsQuery,
    useUnreadCountQuery,
    useMarkAsReadMutation,
    useMarkAllAsReadMutation,
    useDeleteNotificationMutation,
    useDeleteReadNotificationsMutation,
  };
}
